import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Scissors, Crown, Sparkles } from 'lucide-react';

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Tout voir' },
    { id: 'hommes', name: 'Coiffure Hommes' },
    { id: 'femmes', name: 'Tresses Femmes' },
    { id: 'ongles', name: 'Ongles' },
    { id: 'salon', name: 'Le Salon' }
  ];

  const galleryItems = [
    // COIFFURE HOMMES
    {
      id: 1,
      image: '/images/IMG-20251103-WA0023.jpg',
      category: 'hommes',
      title: 'Coupe créative avec motifs',
      description: 'Design artistique avec étoiles et cœurs'
    },
    {
      id: 2,
      image: '/images/IMG-20251103-WA0024.jpg',
      category: 'hommes',
      title: 'Coupe dégradée moderne',
      description: 'Style contemporain et précis'
    },
    {
      id: 3,
      image: '/images/IMG-20251103-WA0025.jpg',
      category: 'hommes',
      title: 'Style Pablo signature',
      description: 'Tresses et dégradé maîtrisés'
    },
    {
      id: 4,
      image: '/images/IMG-20251103-WA0026.jpg',
      category: 'hommes',
      title: 'Coupe artistique créative',
      description: 'Motifs personnalisés et tresses'
    },
    {
      id: 5,
      image: '/images/IMG-20251103-WA0027.jpg',
      category: 'hommes',
      title: 'Coupe classique masculine',
      description: 'Style élégant et soigné'
    },
    {
      id: 6,
      image: '/images/IMG-20251103-WA0028.jpg',
      category: 'hommes',
      title: 'Dégradé parfait',
      description: 'Technique maîtrisée et finition impeccable'
    },
    {
      id: 7,
      image: '/images/IMG-20251103-WA0032.jpg',
      category: 'hommes',
      title: 'Pablo au travail',
      description: 'Maître coiffeur en action'
    },
    
    // TRESSES FEMMES
    {
      id: 8,
      image: '/images/IMG-20251103-WA0031.jpg',
      category: 'femmes',
      title: 'Tresses artistiques',
      description: 'Créations uniques et protectrices'
    },
    
    // ONGLES
    {
      id: 9,
      image: '/images/IMG-20251103-WA0030.jpg',
      category: 'ongles',
      title: 'Manucure French parfaite',
      description: 'Ongles soignés et élégants'
    },
    {
      id: 10,
      image: '/images/IMG-20251103-WA0029.jpg',
      category: 'ongles',
      title: 'Soin des ongles complet',
      description: 'Manucure professionnelle'
    }
  ];

  const filteredItems = selectedCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.category === selectedCategory);

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
            Découvrez nos <span className="text-gradient">réalisations</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            Explorez notre galerie et laissez-vous inspirer par nos créations. 
            Chaque coiffure raconte une histoire, chaque style révèle une personnalité.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Badge
                key={category.id}
                variant={selectedCategory === category.id ? "default" : "outline"}
                className={`cursor-pointer px-4 py-2 text-sm font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border-primary text-primary hover:bg-primary hover:text-primary-foreground'
                }`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredItems.map((item) => (
              <Card key={item.id} className="card-luxury group overflow-hidden">
                <div className="relative">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Category Badge */}
                  <Badge className="absolute top-3 left-3 bg-primary/90 text-primary-foreground">
                    {categories.find(cat => cat.id === item.category)?.name}
                  </Badge>
                  
                  {/* Overlay Content */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                    <p className="text-sm text-gray-200">{item.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground text-lg">
                Aucune réalisation trouvée pour cette catégorie.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Section Spécialités */}
      <section className="py-16 bg-gradient-to-b from-background to-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="heading-lg font-display mb-4">
              Nos <span className="text-gradient">spécialités</span>
            </h2>
            <p className="text-enhanced">
              Découvrez l'expertise de Pablo dans chaque domaine
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Coiffure Hommes */}
            <Card className="card-luxury text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Scissors className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Coiffure Hommes</h3>
                <p className="text-muted-foreground mb-4">
                  Coupes modernes, dégradés parfaits, barbe soignée. 
                  Pablo maîtrise tous les styles masculins.
                </p>
                <div className="text-primary font-semibold">
                  À partir de 15€
                </div>
              </CardContent>
            </Card>

            {/* Tresses Femmes */}
            <Card className="card-luxury text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-400/20 to-purple-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="h-8 w-8 text-pink-400" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Tresses Femmes</h3>
                <p className="text-muted-foreground mb-4">
                  Box braids, nattes collées, tresses artistiques. 
                  Créations uniques et protectrices.
                </p>
                <div className="text-pink-400 font-semibold">
                  À partir de 40€
                </div>
              </CardContent>
            </Card>

            {/* Ongles */}
            <Card className="card-luxury text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-rose-400/20 to-pink-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="h-8 w-8 text-rose-400" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Ongles</h3>
                <p className="text-muted-foreground mb-4">
                  Pose gel, vernis semi-permanent, nail art. 
                  Des mains parfaites et soignées.
                </p>
                <div className="text-rose-400 font-semibold">
                  À partir de 25€
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary/10 to-primary-glow/10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
            Prêt pour votre <span className="text-gradient">transformation</span> ?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Rejoignez nos clients satisfaits et découvrez ce que Pablo Coiffure peut faire pour vous.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/booking"
              className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground font-semibold rounded-lg hover:shadow-xl hover:shadow-primary/30 transform hover:scale-105 transition-all duration-300"
            >
              Réserver maintenant
            </a>
            <a 
              href="https://wa.me/33767632259"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-8 py-3 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-semibold rounded-lg transform hover:scale-105 transition-all duration-300"
            >
              WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;